import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useSearch } from '@tanstack/react-router';
import { CheckCircle2, Loader2, MailX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/utils';
import { BrandLogo } from '@/components/brand/brand-logo';

interface UnsubscribeInfo {
  contactName: string;
  businessName: string;
  alreadyUnsubscribed: boolean;
}

export function UnsubscribePage() {
  const search = useSearch({ strict: false }) as { contact?: string };
  const contactId = search.contact;

  const { data, isLoading, error } = useQuery({
    queryKey: ['unsubscribe', contactId],
    queryFn: () => api<UnsubscribeInfo>(`/api/v1/unsubscribe?contact=${encodeURIComponent(contactId!)}`),
    enabled: !!contactId,
    retry: false,
  });

  const unsubscribe = useMutation({
    mutationFn: () =>
      api<{ ok: boolean; businessName: string }>(
        `/api/v1/unsubscribe?contact=${encodeURIComponent(contactId!)}`,
        { method: 'POST', body: '{}' },
      ),
  });

  if (!contactId) {
    return (
      <UnsubscribeShell>
        <Card title="Invalid link" description="This unsubscribe link is missing required information.">
          <p className="text-sm text-text-secondary">Use the link from your renewal reminder email, or contact the business directly.</p>
        </Card>
      </UnsubscribeShell>
    );
  }

  if (isLoading) {
    return (
      <UnsubscribeShell>
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-brand" />
      </UnsubscribeShell>
    );
  }

  if (error || !data) {
    return (
      <UnsubscribeShell>
        <Card title="Link expired or invalid" description="We couldn't find this subscription. You may already be unsubscribed.">
          <p className="text-sm text-text-secondary">If you still receive emails, use the unsubscribe link in the latest message.</p>
        </Card>
      </UnsubscribeShell>
    );
  }

  const done = data.alreadyUnsubscribed || unsubscribe.isSuccess;

  return (
    <UnsubscribeShell>
      <Card
        title={done ? "You're unsubscribed" : 'Unsubscribe from renewal reminders'}
        description={
          done
            ? `${data.businessName} will no longer email you renewal reminders.`
            : `${data.businessName} sends maintenance renewal reminders to ${data.contactName}.`
        }
      >
        {done ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <CheckCircle2 className="h-10 w-10 text-success" />
            <p className="text-sm text-text-secondary">
              You won't receive further renewal reminder emails from {data.businessName}.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-text-secondary">
              Click below to stop renewal reminder emails. You can still contact {data.businessName} directly
              for service.
            </p>
            <Button
              className="w-full"
              variant="secondary"
              onClick={() => unsubscribe.mutate()}
              loading={unsubscribe.isPending}
            >
              <MailX className="h-4 w-4" /> Unsubscribe
            </Button>
          </div>
        )}
      </Card>
    </UnsubscribeShell>
  );
}

function UnsubscribeShell({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-bg-app">
      <header className="border-b border-border bg-bg-surface px-4 py-4">
        <div className="mx-auto flex max-w-lg items-center justify-center">
          <BrandLogo size="sm" to="/" showName />
        </div>
      </header>
      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-10">{children}</main>
    </div>
  );
}
